import React, { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Client } from '@/types/shelter';
import { Phone, Mail, MapPin, AlertTriangle, Edit, Save, Calendar, CheckCircle, UserCheck, Plus, X, MailOpen, FileText, CalendarDays } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { FamilyMembersSection } from '@/components/FamilyMembersSection';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logActivity } from '@/hooks/useActivityLog';
import { toast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

interface CaseManagerOption {
  id: string;
  full_name: string;
}

interface Reminder {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  type: 'document' | 'followup';
}

interface ClientProfileDetailsProps {
  client: Client;
  editedClient: Client | null;
  isEditing: boolean;
  isStaffView: boolean;
  reminders: Reminder[];
  isFamilyShelter: boolean;
  onEdit: () => void;
  onSave: () => void;
  onEditedClientChange: (client: Client | null) => void;
  onAcknowledgeReminder: (id: string) => void;
}

export const ClientProfileDetails: React.FC<ClientProfileDetailsProps> = ({
  client,
  editedClient,
  isEditing,
  isStaffView,
  reminders,
  isFamilyShelter,
  onEdit,
  onSave,
  onEditedClientChange,
  onAcknowledgeReminder
}) => {
  const { isDemoMode } = useAuth();
  const queryClient = useQueryClient();
  const [caseManagers, setCaseManagers] = useState<CaseManagerOption[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  
  // Alert status fields
  const [alertStatuses, setAlertStatuses] = useState({
    hasMail: false,
    ilpReminder: false,
  });
  
  // Mail/package alert form state
  const [showMailForm, setShowMailForm] = useState(false);
  const [mailType, setMailType] = useState<'mail' | 'package' | 'both'>('mail');
  const [mailQuantity, setMailQuantity] = useState(1);
  const [packageQuantity, setPackageQuantity] = useState(1);
  
  // Reminders
  const [newReminderType, setNewReminderType] = useState('followup');
  const [newReminderDescription, setNewReminderDescription] = useState('');
  const [newReminderDate, setNewReminderDate] = useState('');
  const [showReminderForm, setShowReminderForm] = useState(false);

  // Load alert statuses from alerts table
  useEffect(() => {
    if (isDemoMode || !client.id) return;
    const fetchAlertStatuses = async () => {
      const { data } = await supabase
        .from('alerts')
        .select('title, status')
        .eq('client_id', client.id)
        .eq('status', 'active');
      
      if (data) {
        const statuses = { hasMail: false, ilpReminder: false };
        data.forEach(a => {
          if (a.title.endsWith('has mail')) statuses.hasMail = true;
          if (a.title === 'ILP Reminder') statuses.ilpReminder = true;
        });
        setAlertStatuses(statuses);
      }
    };
    fetchAlertStatuses();
  }, [client.id, isDemoMode]);

  // Fetch case managers (users with case_manager role)
  useEffect(() => {
    if (isDemoMode) return;
    
    const fetchCaseManagers = async () => {
      // Use org-scoped SECURITY DEFINER RPC so all manager-tier roles
      // (including program_director) can populate the dropdown.
      const { data, error } = await (supabase as any).rpc('get_mentionable_entities', { search_text: '' });
      if (error || !Array.isArray(data)) return;

      const managers = data
        .filter((e: any) => e.type === 'user' && e.role === 'case_manager' && e.id && e.name)
        .map((e: any) => ({ id: e.id as string, full_name: e.name as string }));

      // Ensure the currently assigned case manager is always present in the list
      // even if their primary role isn't 'case_manager'.
      if (client.case_worker_id && !managers.some(m => m.id === client.case_worker_id)) {
        const current = data.find((e: any) => e.id === client.case_worker_id);
        if (current?.name) managers.unshift({ id: client.case_worker_id, full_name: current.name });
      }

      setCaseManagers(managers);
    };

    fetchCaseManagers();
  }, [isDemoMode, isStaffView, client.case_worker_id]);

  const handleAssignCaseManager = async (caseManagerId: string) => {
    setIsAssigning(true);
    try {
      const previousId = client.case_worker_id || null;
      const value = caseManagerId === 'unassigned' ? null : caseManagerId;

      const { data, error } = await supabase
        .from('clients')
        .update({ case_worker_id: value })
        .eq('id', client.id)
        .select('id, case_worker_id')
        .single();

      if (error) throw error;
      if (!data) throw new Error("You don't have permission to update this client.");

      await logActivity({
        actionType: 'assign_case_manager',
        actionDetails: {
          client_id: client.id,
          client_name: client.name,
          previous_case_manager_id: previousId,
          new_case_manager_id: value,
        },
        siteId: client.site_id,
      });

      toast({ title: 'Case Manager Updated', description: 'Assignment saved successfully.' });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client-activity-logs', client.id] });
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Error', description: e.message || 'Failed to assign case manager' });
    } finally {
      setIsAssigning(false);
    }
  };

  // (Allergy management removed — Cimfoni stores no PHI)

  // Alert status toggle for non-mail alerts
  const toggleAlertStatus = async (field: keyof typeof alertStatuses, title: string) => {
    if (isDemoMode) return;
    const newValue = !alertStatuses[field];
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (newValue) {
        const { data: existing } = await supabase
          .from('alerts')
          .select('id')
          .eq('client_id', client.id)
          .eq('title', title)
          .single();
        
        if (existing) {
          await supabase.from('alerts').update({ status: 'active' }).eq('id', existing.id);
        } else {
          await supabase.from('alerts').insert({
            client_id: client.id,
            site_id: client.site_id,
            title,
            description: `${title} for ${client.name}`,
            type: 'info',
            category: 'client',
            status: 'active',
            created_by: user?.id,
          });
        }
      } else {
        await supabase
          .from('alerts')
          .update({ status: 'cleared' })
          .eq('client_id', client.id)
          .eq('title', title);
      }
      setAlertStatuses(prev => ({ ...prev, [field]: newValue }));
      toast({ title: newValue ? `${title} Activated` : `${title} Cleared` });
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    }
  };

  // Create mail/package alert with quantity and type
  const createMailAlert = async () => {
    if (isDemoMode) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Build description based on type and quantity
      let description = '';
      if (mailType === 'mail') {
        description = `${mailQuantity} mail item${mailQuantity > 1 ? 's' : ''}`;
      } else if (mailType === 'package') {
        description = `${packageQuantity} package${packageQuantity > 1 ? 's' : ''}`;
      } else {
        // both
        const parts = [];
        if (mailQuantity > 0) parts.push(`${mailQuantity} mail`);
        if (packageQuantity > 0) parts.push(`${packageQuantity} package${packageQuantity > 1 ? 's' : ''}`);
        description = parts.join(', ');
      }

      const alertTitle = mailType === 'mail' 
        ? `${client.name} has mail` 
        : mailType === 'package' 
          ? `${client.name} has package` 
          : `${client.name} has mail & packages`;

      await supabase.from('alerts').insert({
        client_id: client.id,
        site_id: client.site_id,
        title: alertTitle,
        description,
        type: 'info',
        category: 'client',
        status: 'active',
        created_by: user?.id,
      });

      setAlertStatuses(prev => ({ ...prev, hasMail: true }));
      setShowMailForm(false);
      setMailType('mail');
      setMailQuantity(1);
      setPackageQuantity(1);
      toast({ title: 'Mail Alert Created', description: `${client.name} has been notified of ${description}` });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    }
  };

  // Add reminder as alert
  const addReminder = async () => {
    if (!newReminderDescription.trim() || !newReminderDate || isDemoMode) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const typeLabels: Record<string, string> = {
        document: 'Document Reminder',
        followup: 'Follow-up Reminder',
      };
      const reminderDetail = newReminderDescription.trim();
      const dueText = new Date(newReminderDate).toLocaleString();
      const title = typeLabels[newReminderType] || 'Reminder';
      const { error } = await supabase.from('alerts').insert({
        client_id: client.id,
        site_id: client.site_id,
        title,
        description: `Reminder: ${reminderDetail}\nDue: ${dueText}\nClient: ${client.name}`,
        type: 'warning',
        category: 'reminder',
        status: 'active',
        created_by: user?.id,
      });
      if (error) throw error;
      setNewReminderDescription('');
      setNewReminderDate('');
      toast({ title: 'Reminder Added' });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client-reminders', client.id] });
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Failed to add reminder', description: e.message });
    }
  };

  const currentCaseManagerName = (() => {
    if (!client.caseWorker || client.caseWorker === 'Unassigned') return 'Unassigned';
    const found = caseManagers.find(cm => cm.id === client.caseWorker);
    return found?.full_name || client.caseWorker;
  })();

  if (isStaffView) {
    // Simplified staff view
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Client Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Info */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Basic Info</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <p className="text-sm p-2 bg-muted rounded font-medium">{client.name}</p>
                </div>
                <div>
                  <Label>Room</Label>
                  <p className="text-sm p-2 bg-muted rounded font-medium">{client.room || 'Unassigned'}</p>
                </div>
              </div>
              <div className="mt-3">
                <Label className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-muted-foreground" />
                  Case Manager
                </Label>
                {caseManagers.length > 0 ? (
                  <Select
                    value={client.case_worker_id || 'unassigned'}
                    onValueChange={handleAssignCaseManager}
                    disabled={isAssigning}
                  >
                    <SelectTrigger className="bg-background">
                      <SelectValue placeholder="Select case manager" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover z-50">
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {caseManagers.map(cm => (
                        <SelectItem key={cm.id} value={cm.id}>{cm.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm p-2 bg-muted rounded font-medium">{currentCaseManagerName}</p>
                )}
              </div>
            </div>

            <Separator />

            {/* (Allergies removed — Cimfoni stores no PHI) */}


            {/* Reminders */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Reminders</p>
              <div className="space-y-2">
                {reminders.length > 0 ? (
                  reminders.map((reminder) => (
                    <div key={reminder.id} className="flex items-start justify-between p-3 bg-muted rounded-lg border border-border">
                      <div className="flex-1">
                        <p className="font-medium">{reminder.title}</p>
                        <p className="text-sm text-muted-foreground">{reminder.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Due: {new Date(reminder.dueDate).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onAcknowledgeReminder(reminder.id)}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Done
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground p-3 bg-muted rounded">No active reminders</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin view - editable
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Personal Information</CardTitle>
            <Button variant="outline" size="sm" onClick={isEditing ? onSave : onEdit}>
              {isEditing ? <Save className="h-4 w-4 mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
              {isEditing ? 'Save' : 'Edit'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic Info */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Basic Info</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="age">Age</Label>
                {isEditing ? (
                  <Input 
                    id="age" 
                    type="number" 
                    value={editedClient?.age || ''} 
                    onChange={e => onEditedClientChange(editedClient ? {
                      ...editedClient,
                      age: parseInt(e.target.value)
                    } : null)} 
                  />
                ) : (
                  <p className="text-sm p-2 bg-muted rounded">{client.age} years old</p>
                )}
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-muted-foreground" />
                  Case Manager
                </Label>
                {caseManagers.length > 0 ? (
                  <Select
                    value={client.case_worker_id || 'unassigned'}
                    onValueChange={handleAssignCaseManager}
                    disabled={isAssigning}
                  >
                    <SelectTrigger className="bg-background">
                      <SelectValue placeholder="Select case manager" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover z-50">
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {caseManagers.map(cm => (
                        <SelectItem key={cm.id} value={cm.id}>{cm.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm p-2 bg-muted rounded">{currentCaseManagerName}</p>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Contact Information */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Contact Information</p>
            <div className="space-y-3">
              <div>
                <Label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Phone className="h-3.5 w-3.5" />
                  Phone
                </Label>
                {isEditing ? (
                  <Input 
                    value={editedClient?.phone || ''} 
                    onChange={e => onEditedClientChange(editedClient ? {
                      ...editedClient,
                      phone: e.target.value
                    } : null)} 
                    placeholder="Phone number"
                  />
                ) : (
                  <p className="text-sm p-2 bg-muted rounded">{client.phone || 'Not provided'}</p>
                )}
              </div>
              <div>
                <Label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Mail className="h-3.5 w-3.5" />
                  Email
                </Label>
                {isEditing ? (
                  <Input 
                    value={editedClient?.email || ''} 
                    onChange={e => onEditedClientChange(editedClient ? {
                      ...editedClient,
                      email: e.target.value
                    } : null)} 
                    placeholder="Email address"
                  />
                ) : (
                  <p className="text-sm p-2 bg-muted rounded">{client.email || 'Not provided'}</p>
                )}
              </div>
              <div>
                <Label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" />
                  Address
                </Label>
                <p className="text-sm p-2 bg-muted rounded">{client.address || 'Not provided'}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Emergency Contact */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Emergency Contact</p>
            {client.emergencyContact ? (
              <div className="p-3 border border-border rounded-lg bg-muted/50">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <span className="text-xs font-medium text-muted-foreground">Name</span>
                    {isEditing ? (
                      <Input 
                        value={editedClient?.emergencyContact?.name || ''} 
                        onChange={e => onEditedClientChange(editedClient ? {
                          ...editedClient,
                          emergencyContact: {
                            ...editedClient.emergencyContact,
                            name: e.target.value,
                            relationship: editedClient.emergencyContact?.relationship || '',
                            phone: editedClient.emergencyContact?.phone || ''
                          }
                        } : null)} 
                        placeholder="Emergency contact name"
                      />
                    ) : (
                      <p className="text-sm font-medium">{client.emergencyContact.name}</p>
                    )}
                  </div>
                  <div>
                    <span className="text-xs font-medium text-muted-foreground">Relationship</span>
                    {isEditing ? (
                      <Input 
                        value={editedClient?.emergencyContact?.relationship || ''} 
                        onChange={e => onEditedClientChange(editedClient ? {
                          ...editedClient,
                          emergencyContact: {
                            ...editedClient.emergencyContact,
                            name: editedClient.emergencyContact?.name || '',
                            relationship: e.target.value,
                            phone: editedClient.emergencyContact?.phone || ''
                          }
                        } : null)} 
                        placeholder="Relationship"
                      />
                    ) : (
                      <p className="text-sm">{client.emergencyContact.relationship}</p>
                    )}
                  </div>
                  <div>
                    <span className="text-xs font-medium text-muted-foreground">Phone</span>
                    {isEditing ? (
                      <Input 
                        value={editedClient?.emergencyContact?.phone || ''} 
                        onChange={e => onEditedClientChange(editedClient ? {
                          ...editedClient,
                          emergencyContact: {
                            ...editedClient.emergencyContact,
                            name: editedClient.emergencyContact?.name || '',
                            relationship: editedClient.emergencyContact?.relationship || '',
                            phone: e.target.value
                          }
                        } : null)} 
                        placeholder="Phone number"
                      />
                    ) : (
                      <p className="text-sm">{client.emergencyContact.phone}</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground p-3 bg-muted rounded">No emergency contact provided</p>
            )}
          </div>

          {/* (Allergies / Food Allergies removed — Cimfoni stores no PHI) */}


          {/* Alerts & Reminders */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Alerts & Reminders</p>

            {/* Status chip row */}
            <div className="flex flex-wrap gap-2 mb-4">
              {/* Mail / Package chip */}
              <Popover open={showMailForm} onOpenChange={setShowMailForm}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                      alertStatuses.hasMail
                        ? 'bg-primary/10 border-primary/30 text-primary'
                        : 'bg-background border-border text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <MailOpen className="h-3.5 w-3.5" />
                    Mail / Package
                    {alertStatuses.hasMail && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-primary" />}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 bg-popover z-50" align="start">
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Log Mail / Package</p>
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select value={mailType} onValueChange={(v) => setMailType(v as any)}>
                        <SelectTrigger className="bg-background h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-popover z-50">
                          <SelectItem value="mail">Mail</SelectItem>
                          <SelectItem value="package">Package</SelectItem>
                          <SelectItem value="both">Both</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {(mailType === 'mail' || mailType === 'both') && (
                      <div>
                        <Label className="text-xs">Mail Quantity</Label>
                        <Input type="number" min={1} value={mailQuantity} onChange={e => setMailQuantity(Math.max(1, parseInt(e.target.value) || 1))} className="h-8 text-sm" />
                      </div>
                    )}
                    {(mailType === 'package' || mailType === 'both') && (
                      <div>
                        <Label className="text-xs">Package Quantity</Label>
                        <Input type="number" min={1} value={packageQuantity} onChange={e => setPackageQuantity(Math.max(1, parseInt(e.target.value) || 1))} className="h-8 text-sm" />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button size="sm" onClick={createMailAlert} className="flex-1">Create Alert</Button>
                      <Button size="sm" variant="outline" onClick={() => setShowMailForm(false)}>Cancel</Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* ILP chip */}
              <button
                type="button"
                onClick={() => toggleAlertStatus('ilpReminder', 'ILP Reminder')}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                  alertStatuses.ilpReminder
                    ? 'bg-primary/10 border-primary/30 text-primary'
                    : 'bg-background border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                <FileText className="h-3.5 w-3.5" />
                ILP
                {alertStatuses.ilpReminder && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-primary" />}
              </button>

              {/* Appointment chip removed — no PHI / medical reminders */}
            </div>




            <Separator className="my-3" />

            {/* Active Reminders */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" />
                  Active Reminders
                </Label>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => setShowReminderForm(v => !v)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {showReminderForm ? 'Cancel' : 'New reminder'}
                </Button>
              </div>

              <div className="space-y-2">
                {reminders.length > 0 ? (
                  reminders.map((reminder) => (
                    <div key={reminder.id} className="flex items-start justify-between gap-3 p-3 rounded-md border border-border bg-background">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{reminder.title}</p>
                        {reminder.description && (
                          <p className="text-sm text-muted-foreground truncate">{reminder.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Due {new Date(reminder.dueDate).toLocaleString()}
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" className="h-7" onClick={() => onAcknowledgeReminder(reminder.id)}>
                        <CheckCircle className="h-4 w-4 mr-1" /> Done
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground px-1 py-2">No active reminders</p>
                )}

                {/* Inline add form */}
                {showReminderForm && (
                  <div className="border rounded-md p-3 space-y-3 bg-muted/30">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Reminder Type</Label>
                        <Select value={newReminderType} onValueChange={setNewReminderType}>
                          <SelectTrigger className="bg-background h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-popover z-50">
                          <SelectItem value="document">Document Due</SelectItem>
                          <SelectItem value="followup">Follow-up</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Date & Time</Label>
                        <Input
                          type="datetime-local"
                          className="h-9"
                          value={newReminderDate}
                          onChange={e => setNewReminderDate(e.target.value)}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={newReminderDescription}
                        onChange={e => setNewReminderDescription(e.target.value)}
                        placeholder="Reminder details..."
                        className="h-9"
                      />
                    </div>
                    <Button
                      size="sm"
                      onClick={async () => {
                        await addReminder();
                        setShowReminderForm(false);
                      }}
                      disabled={!newReminderDescription.trim() || !newReminderDate}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Reminder
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Returnee Status */}
      {client.isReturnee && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-amber-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Returnee Client
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-amber-700">
              {client.returneeObservations || 'No additional observations'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Family Members Section - shown for all clients in family shelters */}
      {isFamilyShelter && (
        <FamilyMembersSection 
          client={client}
          editedClient={editedClient}
          isEditing={isEditing}
          onUpdateClient={onEditedClientChange as (client: Client) => void}
        />
      )}
    </div>
  );
};